// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color, mergeWithCss, parseBoolean } from "@stoneage/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { getSettingsCollection } from "@stoneage/App/Store/Selectors/SettingsSelectors";
import { connect } from "react-redux";
import { css } from "styled-components";
import { fontConfig } from "@stoneage/XDL/constants";
import IconSignout from "@stoneage/XDL/Icons/IconSignout";
import {
    trackAsImpersonation,
    trackGaMyAccountFlyOutEvent,
    trackGaMyAccountPagesNavEvent,
} from "@stoneage/App/Utilities/DataLayer";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";

export interface MappedLink {
    title?: string;
    url?: string;
    openInNewWindow?: boolean;
    excludeFromNavigation?: boolean;
}

interface MiniSignInProps {
    linksField: LinkModel[];
    displayChangeCustomerLink?: boolean;
    isPunchOutSession?: boolean;
    displayPricingAndInventory?: boolean;
    showInventoryToggle?: boolean;
    showPricingToggle?: boolean;
    showInventory?: boolean;
    showPricing?: boolean;
    onSignOutHandler?: () => void;
    priceInventoryToggleHandler?: (type: "Price" | "Inventory") => void;
    extendedStyles?: MiniMyAccountStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const customSettings = getSettingsCollection(state).customSettings;
    const session = getSession(state);
    return {
        isSentinelUser: parseBoolean(session.properties.isSentinelUser),
        isThinkificUser: parseBoolean(session.properties.isThinkificUser),
        sentinelUrl: customSettings.sentinelHeaderUrl,
        thinkificlUrl: customSettings.thinkificHeaderUrl,
        billTo: getBillToState(state, session.billToId).value,
        salesPersonName: session.properties.salesPersonName,
        salesPersonPhone: session.properties.salesPersonPhone,
        myAccountPagesLinksForTracking: {
            orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage")?.url,
            invoiceHistoryPageLink: getPageLinkByPageType(state, "InvoiceHistoryPage")?.url,
            myQuotesPageLink: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
            myProfilePageLink: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
            userAdministrationPageLink: getPageLinkByPageType(state, "UserListPage")?.url,
            sentinelPageLink: customSettings.sentinelHeaderUrl,
            courseManagementPageLink: getPageLinkByPageType(state, "CourseManagementPage")?.url,
            myCoursesPageLink: getPageLinkByPageType(state, "MyCoursesPage")?.url,
        },
        impersonation: trackAsImpersonation(state),
    };
};

type Props = MiniSignInProps & ReturnType<typeof mapStateToProps>;

export interface MiniMyAccountStyles {
    container?: InjectableCss;
    linksContainer?: InjectableCss;
    itemLink?: LinkPresentationProps;
    customerContainer?: InjectableCss;
    customerItemContainer?: InjectableCss;
    customerLabel?: TypographyPresentationProps;
    customerValue?: TypographyPresentationProps;
    customerLink?: LinkPresentationProps;
    toggleClickable?: ClickablePresentationProps;
    signOutContainer?: InjectableCss;
    actionLink?: LinkPresentationProps;
}

export const miniMyAccountStyles: MiniMyAccountStyles = {
    linksContainer: {
        css: css`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px 24px;
        `,
    },
    itemLink: {
        color: "secondary",
        typographyProps: {
            ...fontConfig.xSmallSingleLine,
        },
    },
    customerContainer: {
        css: css`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px 24px;
            margin-top: 24px;
            padding-top: 24px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    customerItemContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
        `,
    },
    customerLabel: {
        variant: "h6",
        css: css`
            text-transform: capitalize;
            margin-bottom: 0;
        `,
    },
    customerValue: {
        ...fontConfig.xSmallSingleLine,
    },
    customerLink: {
        color: "text.main",
        typographyProps: {
            ...fontConfig.xSmallSingleLine,
        },
        css: css`
            align-content: baseline;
        `,
    },
    signOutContainer: {
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 12px 24px;
            flex-wrap: wrap;
            margin-top: 24px;
            padding-top: 24px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    actionLink: {
        color: "text.link",
        icon: {
            iconProps: {
                src: IconSignout,
                size: 15,
            },
        },
        typographyProps: {
            ...fontConfig.regularSingleLine,
            weight: fontConfig.weight.medium,
        },
    },
    toggleClickable: {},
};

const MiniMyAccount: FC<Props> = ({
    linksField,
    isSentinelUser,
    isThinkificUser,
    sentinelUrl,
    thinkificlUrl,
    billTo,
    salesPersonName,
    salesPersonPhone,
    isPunchOutSession,
    onSignOutHandler,
    myAccountPagesLinksForTracking,
    impersonation,
    displayPricingAndInventory,
    showInventoryToggle,
    showPricingToggle,
    showInventory,
    showPricing,
    priceInventoryToggleHandler,
    extendedStyles,
}) => {
    const { links } = useGetLinks(linksField, o => o.fields.destination);
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));

    const filteredLinks = (links as MappedLink[])
        .map((link, idx) => ({ ...link, title: linksField[idx].fields.overriddenTitle || link.title }))
        .filter(({ url, excludeFromNavigation }) => {
            if (!url || excludeFromNavigation) return false;
            if (url === sentinelUrl && !isSentinelUser) return false;
            if (url === thinkificlUrl && !isThinkificUser) return false;
            return true;
        });

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.linksContainer}>
                {filteredLinks.map((link, idx) => (
                    <StyledWrapper key={`${link.title}_${link.url}_${idx}`}>
                        <Link
                            href={link.url}
                            title={link.title}
                            {...styles.itemLink}
                            onClick={() => {
                                // Track GA4
                                if (link.url) {
                                    trackGaMyAccountFlyOutEvent(
                                        link.url,
                                        link.title || "",
                                        impersonation,
                                        billTo?.customerNumber,
                                    );
                                    trackGaMyAccountPagesNavEvent(
                                        null,
                                        link.url,
                                        link.title || "",
                                        impersonation,
                                        billTo?.customerNumber,
                                        myAccountPagesLinksForTracking,
                                    );
                                }
                            }}
                        >
                            {link.title}
                        </Link>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
            <StyledWrapper {...styles.customerContainer}>
                {billTo?.companyName && (
                    <StyledWrapper {...styles.customerItemContainer}>
                        <Typography {...styles.customerLabel}>{translate("Customer")}</Typography>
                        <Typography {...styles.customerValue} translate="no">
                            {billTo?.companyName}
                        </Typography>
                    </StyledWrapper>
                )}
                {(salesPersonName || salesPersonPhone) && (
                    <StyledWrapper {...styles.customerItemContainer}>
                        <Typography {...styles.customerLabel}>{translate("Sales Rep")}</Typography>
                        {salesPersonName && (
                            <Typography {...styles.customerValue} translate="no">
                                {salesPersonName}
                            </Typography>
                        )}
                        {salesPersonPhone && (
                            <Link href={`tel:${salesPersonPhone}`} {...styles.customerLink}>
                                {salesPersonPhone}
                            </Link>
                        )}
                    </StyledWrapper>
                )}
                {billTo?.customerNumber && (
                    <StyledWrapper {...styles.customerItemContainer}>
                        <Typography {...styles.customerLabel}>{translate("Account Number")}</Typography>
                        <Typography {...styles.customerValue} translate="no">
                            {billTo?.customerNumber}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.signOutContainer}>
                {showInventoryToggle && displayPricingAndInventory && (
                    <Link
                        {...styles.actionLink}
                        icon={{
                            iconProps: {
                                ...styles.actionLink,
                                src: showInventory ? EyeOff : Eye,
                            },
                        }}
                        onClick={() => priceInventoryToggleHandler?.("Inventory")}
                    >
                        {translate("Inventory")}
                    </Link>
                )}
                {showPricingToggle && displayPricingAndInventory && (
                    <Link
                        {...styles.actionLink}
                        icon={{
                            iconProps: {
                                ...styles.actionLink,
                                src: showPricing ? EyeOff : Eye,
                            },
                        }}
                        onClick={() => priceInventoryToggleHandler?.("Price")}
                    >
                        {translate("Pricing")}
                    </Link>
                )}
                <Link {...styles.actionLink} onClick={onSignOutHandler}>
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(MiniMyAccount);
